import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { 
  FileText, 
  Download, 
  BookOpen, 
  Briefcase, 
  Target, 
  HelpCircle,
  Search,
  Filter,
  Eye,
  Share2,
  Lock,
  CheckCircle,
  Calendar,
  FileDown
} from 'lucide-react';
import { Button } from '../components/ui/button';

const AdminResourceLibrary = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [user, setUser] = useState(null);

  useEffect(() => {
    // Check if user is admin
    const token = localStorage.getItem('xchange_token');
    const userData = localStorage.getItem('xchange_user');
    
    if (!token) {
      navigate('/xchange/login');
      return;
    }
    
    if (userData) {
      const parsedUser = JSON.parse(userData);
      if (parsedUser.role !== 'admin') {
        alert('Access Denied: Admin privileges required');
        navigate('/');
        return;
      }
      setUser(parsedUser);
    }
  }, [navigate]);

  const resources = [
    {
      id: 1,
      title: 'TMG Impact - Investor Pitch Deck',
      description: 'Comprehensive 20-slide investor presentation covering market opportunity, business model, traction, financials, and team. Perfect for seed round discussions.',
      category: 'Business',
      file: 'TMG_IMPACT_INVESTOR_PITCH_DECK.md',
      format: 'Markdown',
      pages: '20 slides',
      lastUpdated: 'November 2024',
      size: '~8,000 words',
      icon: Briefcase,
      color: '#14B8A6',
      tags: ['Investors', 'Funding', 'Pitch', 'Financial Projections']
    },
    {
      id: 2,
      title: 'TMG Impact - Business Model',
      description: 'Detailed business model canvas covering customer segments, value propositions, revenue streams, competitive advantages, and go-to-market strategy.',
      category: 'Business',
      file: 'TMG_IMPACT_BUSINESS_MODEL.md',
      format: 'Markdown',
      pages: '25+ pages',
      lastUpdated: 'November 2024',
      size: '~15,000 words',
      icon: Target,
      color: '#1E3A5F',
      tags: ['Strategy', 'Revenue Model', 'Market Analysis']
    },
    {
      id: 3,
      title: 'TMG Impact - Brand Positioning',
      description: 'Complete brand positioning framework including value propositions, target personas, competitive positioning, messaging, and tone of voice guidelines.',
      category: 'Marketing',
      file: 'TMG_IMPACT_POSITIONING.md',
      format: 'Markdown',
      pages: '18+ pages',
      lastUpdated: 'November 2024',
      size: '~12,000 words',
      icon: BookOpen,
      color: '#14B8A6',
      tags: ['Branding', 'Messaging', 'Marketing', 'Personas']
    },
    {
      id: 4,
      title: 'CSR Policy - FAQ',
      description: 'Comprehensive FAQ covering CSR legal framework, compliance, Schedule VII activities, project selection, fund management, NGO partnerships, and impact measurement.',
      category: 'Knowledge',
      file: 'CSR_POLICY_FAQ.md',
      format: 'Markdown',
      pages: '30+ Q&As',
      lastUpdated: 'November 2024',
      size: '~10,000 words',
      icon: HelpCircle,
      color: '#2A8378',
      tags: ['CSR', 'Compliance', 'Legal', 'FAQ']
    },
    {
      id: 5,
      title: 'Pitch Deck Conversion Guide',
      description: 'Step-by-step guide to convert markdown pitch deck to PowerPoint/Google Slides with design guidelines, templates, and best practices.',
      category: 'Guide',
      file: 'PITCH_DECK_CONVERSION_GUIDE.md',
      format: 'Markdown',
      pages: '15 pages',
      lastUpdated: 'November 2024',
      size: '~5,000 words',
      icon: FileText,
      color: '#D1A85B',
      tags: ['Guide', 'Design', 'Presentation']
    }
  ];

  const categories = [
    { id: 'all', name: 'All Resources', count: resources.length },
    { id: 'Business', name: 'Business Documents', count: resources.filter(r => r.category === 'Business').length },
    { id: 'Marketing', name: 'Marketing Materials', count: resources.filter(r => r.category === 'Marketing').length },
    { id: 'Knowledge', name: 'Knowledge Base', count: resources.filter(r => r.category === 'Knowledge').length },
    { id: 'Guide', name: 'Guides & Templates', count: resources.filter(r => r.category === 'Guide').length }
  ];

  const filteredResources = resources.filter(resource => {
    const matchesSearch = resource.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         resource.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         resource.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesCategory = selectedCategory === 'all' || resource.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const handleDownload = async (file, format = 'md') => {
    try {
      const token = localStorage.getItem('xchange_token');
      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      
      let endpoint;
      if (format === 'pdf') {
        endpoint = `${apiUrl}/api/resources/pdf/${file}`;
      } else {
        endpoint = `${apiUrl}/api/resources/download/${file}`;
      }
      
      const response = await fetch(endpoint, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Download failed');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = format === 'pdf' ? file.replace('.md', '.pdf') : file;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Download error:', error);
      alert('Failed to download file. Please try again.');
    }
  };

  const handleViewOnline = async (file) => {
    try {
      const token = localStorage.getItem('xchange_token');
      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      const endpoint = `${apiUrl}/api/resources/view/${file}`;
      
      const response = await fetch(endpoint, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('View failed');
      }
      
      const content = await response.text();
      
      // Open in new window with markdown rendering
      const newWindow = window.open('', '_blank');
      newWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>${file}</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              max-width: 900px;
              margin: 40px auto;
              padding: 20px;
              line-height: 1.6;
              color: #1E3A5F;
            }
            h1 { color: #14B8A6; border-bottom: 3px solid #14B8A6; padding-bottom: 10px; }
            h2 { color: #1E3A5F; border-left: 4px solid #14B8A6; padding-left: 12px; }
            h3 { color: #1E3A5F; }
            code { background-color: #f4f4f4; padding: 2px 6px; border-radius: 3px; }
            pre { background-color: #f4f4f4; padding: 15px; border-radius: 5px; overflow-x: auto; }
            table { border-collapse: collapse; width: 100%; margin: 20px 0; }
            th { background-color: #14B8A6; color: white; padding: 10px; text-align: left; }
            td { border: 1px solid #ddd; padding: 8px; }
            tr:nth-child(even) { background-color: #f8f9fa; }
            blockquote { border-left: 4px solid #14B8A6; padding-left: 15px; color: #555; }
          </style>
        </head>
        <body>
          <pre style="white-space: pre-wrap; font-family: Arial, sans-serif; background: none; padding: 0;">${content.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</pre>
        </body>
        </html>
      `);
      newWindow.document.close();
      
    } catch (error) {
      console.error('View error:', error);
      alert('Failed to view file. Please try again.');
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#14B8A6]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      
      {/* Hero Section */}
      <div className="pt-20 bg-gradient-to-br from-[#1E3A5F] to-[#14B8A6]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{background: 'rgba(255, 255, 255, 0.2)'}}>
                  <Lock className="text-white" size={24} />
                </div>
                <span className="px-3 py-1 rounded-full text-xs font-semibold" style={{background: 'rgba(255, 255, 255, 0.2)', color: 'white'}}>
                  Admin Only
                </span>
              </div>
              <h1 className="text-4xl font-bold text-white mb-4">
                Resource Library
              </h1>
              <p className="text-xl text-white/90 max-w-2xl">
                Centralized repository of all TMG Impact documents, presentations, and knowledge resources
              </p>
            </div>
            <div className="hidden md:block">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6">
                <div className="text-3xl font-bold text-white mb-1">{resources.length}</div>
                <div className="text-sm text-white/80">Total Resources</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filter Bar */}
      <div className="bg-white border-b border-slate-200 sticky top-20 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search Bar */}
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
              <input
                type="text"
                placeholder="Search resources, tags, or keywords..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#14B8A6] focus:border-transparent"
              />
            </div>
            
            {/* Category Filter */}
            <div className="flex items-center gap-2">
              <Filter className="text-slate-400" size={20} />
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#14B8A6] focus:border-transparent"
              >
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name} ({cat.count})
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Stats Bar */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl p-6 border border-slate-200">
            <div className="flex items-center justify-between mb-2">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{background: 'rgba(20, 184, 166, 0.1)'}}>
                <Briefcase className="text-[#14B8A6]" size={20} />
              </div>
            </div>
            <div className="text-2xl font-bold text-[#1E3A5F] mb-1">2</div>
            <div className="text-sm text-slate-600">Business Docs</div>
          </div>

          <div className="bg-white rounded-xl p-6 border border-slate-200">
            <div className="flex items-center justify-between mb-2">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{background: 'rgba(20, 184, 166, 0.1)'}}>
                <BookOpen className="text-[#14B8A6]" size={20} />
              </div>
            </div>
            <div className="text-2xl font-bold text-[#1E3A5F] mb-1">1</div>
            <div className="text-sm text-slate-600">Marketing Materials</div>
          </div>

          <div className="bg-white rounded-xl p-6 border border-slate-200">
            <div className="flex items-center justify-between mb-2">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{background: 'rgba(20, 184, 166, 0.1)'}}>
                <HelpCircle className="text-[#14B8A6]" size={20} />
              </div>
            </div>
            <div className="text-2xl font-bold text-[#1E3A5F] mb-1">1</div>
            <div className="text-sm text-slate-600">Knowledge Base</div>
          </div>

          <div className="bg-white rounded-xl p-6 border border-slate-200">
            <div className="flex items-center justify-between mb-2">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{background: 'rgba(20, 184, 166, 0.1)'}}>
                <FileText className="text-[#14B8A6]" size={20} />
              </div>
            </div>
            <div className="text-2xl font-bold text-[#1E3A5F] mb-1">1</div>
            <div className="text-sm text-slate-600">Guides</div>
          </div>
        </div>

        {/* Resources Grid */}
        {filteredResources.length === 0 ? (
          <div className="text-center py-20">
            <FileText className="mx-auto text-slate-300 mb-4" size={64} />
            <h3 className="text-xl font-semibold text-slate-600 mb-2">No resources found</h3>
            <p className="text-slate-500">Try adjusting your search or filter criteria</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6">
            {filteredResources.map((resource) => {
              const IconComponent = resource.icon;
              return (
                <div key={resource.id} className="bg-white rounded-xl border-2 border-slate-200 hover:border-[#14B8A6] hover:shadow-lg transition-all p-6">
                  <div className="flex flex-col md:flex-row gap-6">
                    {/* Icon Section */}
                    <div className="flex-shrink-0">
                      <div className="w-20 h-20 rounded-2xl flex items-center justify-center shadow-lg" style={{background: `linear-gradient(135deg, ${resource.color} 0%, ${resource.color}dd 100%)`}}>
                        <IconComponent className="text-white" size={36} strokeWidth={2} />
                      </div>
                    </div>

                    {/* Content Section */}
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="text-xl font-bold text-[#1E3A5F] mb-2">{resource.title}</h3>
                          <p className="text-slate-600 mb-3">{resource.description}</p>
                        </div>
                      </div>

                      {/* Tags */}
                      <div className="flex flex-wrap gap-2 mb-4">
                        {resource.tags.map((tag, index) => (
                          <span key={index} className="px-3 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
                            {tag}
                          </span>
                        ))}
                      </div>

                      {/* Metadata */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                        <div>
                          <div className="text-xs text-slate-500 mb-1">Format</div>
                          <div className="text-sm font-semibold text-slate-700">{resource.format}</div>
                        </div>
                        <div>
                          <div className="text-xs text-slate-500 mb-1">Size</div>
                          <div className="text-sm font-semibold text-slate-700">{resource.size}</div>
                        </div>
                        <div>
                          <div className="text-xs text-slate-500 mb-1">Pages</div>
                          <div className="text-sm font-semibold text-slate-700">{resource.pages}</div>
                        </div>
                        <div>
                          <div className="flex items-center gap-1">
                            <Calendar size={14} className="text-slate-400" />
                            <div className="text-xs text-slate-500">{resource.lastUpdated}</div>
                          </div>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex flex-wrap gap-3">
                        <Button
                          onClick={() => handleDownload(resource.file, 'pdf')}
                          className="text-white"
                          style={{background: '#14B8A6'}}
                        >
                          <FileDown size={18} className="mr-2" />
                          Download PDF
                        </Button>
                        <Button
                          onClick={() => handleDownload(resource.file, 'md')}
                          variant="outline"
                          style={{borderColor: '#14B8A6', color: '#14B8A6'}}
                        >
                          <Download size={18} className="mr-2" />
                          Download MD
                        </Button>
                        <Button
                          onClick={() => handleViewOnline(resource.file)}
                          variant="outline"
                        >
                          <Eye size={18} className="mr-2" />
                          View Online
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Usage Guidelines */}
        <div className="mt-12 bg-gradient-to-br from-[#14B8A6] to-[#0D9488] rounded-2xl p-8 text-white">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{background: 'rgba(255, 255, 255, 0.2)'}}>
              <CheckCircle size={24} />
            </div>
            <div>
              <h3 className="text-2xl font-bold mb-2">Usage Guidelines</h3>
              <p className="text-white/90">Important information about using these resources</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold mb-3 text-lg">✅ Permitted Use</h4>
              <ul className="space-y-2 text-white/90">
                <li>• Share with internal team members</li>
                <li>• Use for investor presentations</li>
                <li>• Reference for partnership discussions</li>
                <li>• Adapt for specific client needs</li>
                <li>• Convert to PDF/PPT formats</li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-3 text-lg">❌ Restrictions</h4>
              <ul className="space-y-2 text-white/90">
                <li>• Do not share publicly without approval</li>
                <li>• Maintain confidentiality of financial data</li>
                <li>• Obtain permission before external distribution</li>
                <li>• Keep documents up-to-date</li>
                <li>• Report any security concerns immediately</li>
              </ul>
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-white/20">
            <p className="text-sm text-white/80">
              <strong>Note:</strong> All documents are in Markdown format (.md). Use the Pitch Deck Conversion Guide to convert to PowerPoint or PDF.
              For assistance or questions, contact the admin team.
            </p>
          </div>
        </div>

        {/* Conversion Instructions */}
        <div className="mt-8 bg-white rounded-xl p-8 border border-slate-200">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{background: 'rgba(20, 184, 166, 0.1)'}}>
              <FileDown className="text-[#14B8A6]" size={24} />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-[#1E3A5F] mb-2">How to Convert to PDF/PPT</h3>
              <p className="text-slate-600">Follow these steps to convert markdown files to presentation formats</p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="border-l-4 border-[#14B8A6] pl-4">
              <div className="font-semibold text-[#1E3A5F] mb-2">Option 1: Canva (Quick)</div>
              <p className="text-sm text-slate-600">
                Go to Canva.com → Choose pitch deck template → Copy-paste content → Customize → Export
              </p>
            </div>

            <div className="border-l-4 border-[#14B8A6] pl-4">
              <div className="font-semibold text-[#1E3A5F] mb-2">Option 2: PowerPoint</div>
              <p className="text-sm text-slate-600">
                Open PowerPoint → Create slides → Copy content from markdown → Add visuals → Export as PDF
              </p>
            </div>

            <div className="border-l-4 border-[#14B8A6] pl-4">
              <div className="font-semibold text-[#1E3A5F] mb-2">Option 3: Hire Designer</div>
              <p className="text-sm text-slate-600">
                Fiverr/Upwork → Search "pitch deck design" → Provide markdown file → Get professional deck
              </p>
            </div>
          </div>

          <div className="mt-6 bg-slate-50 rounded-lg p-4">
            <p className="text-sm text-slate-700">
              <strong>💡 Tip:</strong> Download the "Pitch Deck Conversion Guide" above for detailed step-by-step instructions with design guidelines.
            </p>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AdminResourceLibrary;